import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col } from 'react-bootstrap';
import {
    makeStyles, Button, Grid, Typography, fade, Paper, Table, TableBody, TableCell, TableRow,
    TableHead, Tabs, Tab,
} from '@material-ui/core';
import Select from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import ButtonStyles from 'styles/theme/Button';
import { CloudUploadOutlinedIcon, SaveIcon } from 'components/icons';
import useManheimSetting from './useManheimSetting';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme, fade));
const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
    },
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        border: `solid 1px ${theme.palette.border.mercury}`,
    },
    actions: {
        margin: theme.spacing(2),
    },
    message: {
        marginTop: theme.spacing(1),
    },
    summaryCard: {
        width: '100%',
        padding: theme.spacing(1.5),
        borderColor: theme.palette.border.mercury,
    },
    summaryTable: {
        marginTop: theme.spacing(1),
    },
    summaryLabel: {
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.border.mercury}`,
    },
    summaryCount: {
        width: 96,
        textAlign: 'right',
        fontWeight: 600,
        borderBottom: `1px solid ${theme.palette.border.mercury}`,
    },
    errorTableContainer: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
    errorDialogPaper: {
        display: 'flex',
        flexDirection: 'column',
        width: '70vw',
        maxWidth: '70vw',
        minWidth: '70vw',
        height: '70vh',
        maxHeight: '70vh',
        minHeight: '70vh',
        overflow: 'hidden',
        '@media (max-width: 600px)': {
            width: '94vw',
            maxWidth: '94vw',
            minWidth: '94vw',
            height: '80vh',
            maxHeight: '80vh',
            minHeight: '80vh',
        },
    },
    errorDialogBody: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
    },
    showErrorsLink: {
        minWidth: 0,
        padding: theme.spacing(0.5),
        color: theme.palette.primary.main,
        fontSize: theme.typography.caption.fontSize,
        textDecoration: 'underline',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    errorMessage: {
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
    },
}));

const ManheimSetting = ({ canWrite }) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
    const [errorTab, setErrorTab] = React.useState(0);

    const {
        manheimClientId,
        manheimDefaultLot,
        loading,
        hasLoadedCompanySettings,
        availableLots,
        selectedLot,
        manheimSellerCustomField,
        manheimLocationStatusCustomField,
        customFieldOptions,
        manheimUserImport,
        companyCode,
        importDialogOpen,
        isImporting,
        importType,
        importResult,
        importError,
        importTitle,
        hasImportResult,
        hasErrors,
        inventoryErrorRows,
        invoiceErrorRows,
        importSummaryRows,
        inventorySummaryRows,
        invoiceSummaryRows,
        setManheimClientId,
        setManheimDefaultLot,
        handleSubmit,
        setManheimSellerCustomField,
        setManheimLocationStatusCustomField,
        handleImportVehicles,
        handleImportInvoices,
        handleImportAll,
        closeImportDialog,
    } = useManheimSetting(canWrite);

    const renderImportSummary = (title, summaryRows) => (
        <Grid item xs={12} key={title}>
            <Paper variant="outlined" className={classes.summaryCard}>
                <Typography variant="subtitle1">{title}</Typography>
                <Table size="small" className={classes.summaryTable}>
                    <TableBody>
                        {summaryRows.map((row) => (
                            <TableRow key={row.label}>
                                <TableCell className={classes.summaryLabel}>{row.label}</TableCell>
                                <TableCell className={classes.summaryCount}>{row.count}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Grid>
    );

    return (
        <div className={classes.root}>
            <Grid container className={classes.box}>
                <Grid item xs={12}>
                    <Form onSubmit={(event) => event.preventDefault()}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <Form.Group as={Col} controlId="manheimClientId">
                                    <Form.Label>Client Id</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Client Id"
                                        value={manheimClientId}
                                        disabled={!canWrite || loading}
                                        onChange={(event) => setManheimClientId(event.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group as={Col} controlId="manheimDefaultLot">
                                    <Form.Label>Default Lot</Form.Label>
                                    <Select
                                        name="manheimDefaultLot"
                                        value={selectedLot?.value ?? String(manheimDefaultLot)}
                                        options={availableLots}
                                        placeholder="Select"
                                        maxMenuHeight={140}
                                        disabled={!canWrite || loading}
                                        onChange={(_, value) => {
                                            const selectedOption = availableLots.find((option) => option.value === value);
                                            setManheimDefaultLot(selectedOption?.label ?? value);
                                        }}
                                    />
                                </Form.Group>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Form.Group as={Col} controlId="manheimSellerCustomField">
                                    <Form.Label>Seller Custom Field</Form.Label>
                                    <Select
                                        name="manheimSellerCustomField"
                                        value={String(manheimSellerCustomField)}
                                        options={customFieldOptions}
                                        placeholder="Select"
                                        maxMenuHeight={140}
                                        onChange={(_, value) => setManheimSellerCustomField(value)}
                                        disabled={!canWrite || loading}
                                    />
                                </Form.Group>

                                <Form.Group as={Col} controlId="manheimLocationStatusCustomField">
                                    <Form.Label>Location Status Custom Field</Form.Label>
                                    <Select
                                        name="manheimLocationStatusCustomField"
                                        value={String(manheimLocationStatusCustomField)}
                                        options={customFieldOptions}
                                        placeholder="Select"
                                        maxMenuHeight={140}
                                        onChange={(_, value) => setManheimLocationStatusCustomField(value)}
                                        disabled={!canWrite || loading}
                                    />
                                </Form.Group>
                            </Grid>
                        </Grid>
                    </Form>
                </Grid>

                {!loading && !manheimUserImport && (
                    <Grid item xs={12} className={classes.message}>
                        <Typography color="error">
                            The Manheim import user is not configured. Please contact an administrator to set it up.
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12} className={classes.actions}>
                    <Grid container direction="column" spacing={1}>
                        <Grid item xs={12}>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.containedSecondaryInfo}
                                startIcon={<SaveIcon />}
                                disabled={!canWrite || loading || !hasLoadedCompanySettings}
                                onClick={handleSubmit}
                            >
                                Save
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.containedInfo}
                                startIcon={<CloudUploadOutlinedIcon />}
                                disabled={!canWrite || loading || isImporting || !manheimUserImport || !companyCode}
                                onClick={handleImportVehicles}
                            >
                                Import Vehicles From Manheim
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.containedInfo}
                                startIcon={<CloudUploadOutlinedIcon />}
                                disabled={!canWrite || loading || isImporting || !manheimUserImport || !companyCode}
                                onClick={handleImportInvoices}
                            >
                                Import Invoices From Manheim
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.containedInfo}
                                startIcon={<CloudUploadOutlinedIcon />}
                                disabled={!canWrite || loading || isImporting || !manheimUserImport || !companyCode}
                                onClick={handleImportAll}
                            >
                                Import All From Manheim
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmDialog
                open={importDialogOpen}
                loading={isImporting}
                title={`Import ${importTitle} From Manheim`}
                titlePrimary="Close"
                hiddenSecondaryButton
                onClickPrimary={closeImportDialog}
                onClose={closeImportDialog}
            >
                {importError && (
                    <Typography color="error" style={{ whiteSpace: 'pre-line' }}>
                        {importError}
                    </Typography>
                )}
                {hasImportResult && !importError && (
                    <Grid container spacing={1}>
                        {importType === 'all' ? (
                            <>
                                {renderImportSummary('Inventory import', inventorySummaryRows)}
                                {importResult.invoice?.failed ? (
                                    <Grid item xs={12}>
                                        <Typography color="error">
                                            Invoice import failed:
                                            {' '}
                                            {importResult.invoice.message}
                                        </Typography>
                                    </Grid>
                                ) : renderImportSummary('Invoice import', invoiceSummaryRows)}
                            </>
                        ) : renderImportSummary(
                            'Import completed',
                            importSummaryRows,
                        )}
                        {hasErrors && (
                            <Grid item xs={12} container justifyContent="flex-end">
                                <Button
                                    variant="text"
                                    className={classes.showErrorsLink}
                                    onClick={() => {
                                        setErrorTab(0);
                                        setErrorDialogOpen(true);
                                    }}
                                >
                                    Show errors
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}
            </ConfirmDialog>

            <ConfirmDialog
                open={errorDialogOpen}
                classes={{
                    dialog: classes.errorDialogPaper,
                    body: classes.errorDialogBody,
                }}
                title="Manheim Import Errors"
                titlePrimary="Close"
                hiddenSecondaryButton
                onClickPrimary={() => setErrorDialogOpen(false)}
                onClose={() => setErrorDialogOpen(false)}
            >
                <Tabs
                    value={errorTab}
                    onChange={(_, value) => setErrorTab(value)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab label={`Inventory (${inventoryErrorRows.length})`} />
                    <Tab label={`Invoice (${invoiceErrorRows.length})`} />
                </Tabs>
                <div className={classes.errorTableContainer}>
                    <Table size="small" aria-label="Manheim import errors">
                        <TableHead>
                            <TableRow>
                                <TableCell>VIN</TableCell>
                                <TableCell>Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(errorTab === 0 ? inventoryErrorRows : invoiceErrorRows).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center">
                                        No errors for this import.
                                    </TableCell>
                                </TableRow>
                            ) : (errorTab === 0 ? inventoryErrorRows : invoiceErrorRows).map((error, index) => (
                                <TableRow key={`${error.vin}-${index}`}>
                                    <TableCell>{error.vin || '—'}</TableCell>
                                    <TableCell className={classes.errorMessage}>{error.message}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </ConfirmDialog>
        </div>
    );
};

ManheimSetting.propTypes = {
    canWrite: PropTypes.bool.isRequired,
};

export default ManheimSetting;
